// The family member list under an event's "+" on Registrations > Select
// Events (desktop). The page clips whatever is below the window (.content
// is overflow: hidden, so nothing down there can be scrolled to), and the
// menu was placed once, when it opened. A family with more members, or a
// list that grew while it was open (a new profile added, or its date of
// birth and gender fields), ran past the bottom of the window with CLOSE
// and REGISTER out of reach (Amanda, 2026-10-20, Jam 13749fec). Now the
// menu always fits in the window, above or below the "+", whichever has
// more room, and the member list scrolls inside it.

// .members-list's own max-height (NewRegistration.vue) and one member row
export const MEMBERS_LIST_MAX = 300;
export const MEMBERS_LIST_MIN = 64;
// kept clear between the menu and the edge of the window
export const WINDOW_MARGIN = 10;

type Rect = { top: number; bottom: number; height: number };
type Box = { getBoundingClientRect: () => Rect };

// the member list's height, so that the menu (its header, new profile and
// buttons, and the list) fits in the window on the side of the "+" with
// more room; the gap between the "+" and the menu is the page's own
export const membersListHeight = (options: {
  windowHeight: number;
  toggle: Rect;
  menu: Rect;
  list: Rect;
  above: boolean;
}) => {
  const { windowHeight, toggle, menu, list, above } = options;
  const gap = Math.max(
    0,
    above ? toggle.top - menu.bottom : menu.top - toggle.bottom
  );
  const fixed = menu.height - list.height;
  const room = Math.max(
    windowHeight - WINDOW_MARGIN - toggle.bottom - gap,
    toggle.top - gap - WINDOW_MARGIN
  );
  return Math.max(
    MEMBERS_LIST_MIN,
    Math.min(MEMBERS_LIST_MAX, Math.floor(room - fixed))
  );
};

// how far the menu is outside the window
export const outsideWindow = (menu: Rect, windowHeight: number) =>
  Math.max(0, Math.ceil(menu.bottom - (windowHeight - WINDOW_MARGIN))) +
  Math.max(0, Math.ceil(WINDOW_MARGIN - menu.top));

// Fits an open menu in the window: caps its member list, has the dropdown
// placed again (place: Bootstrap's update, which flips it above the "+" when
// it fits there and not below), and caps the list a little more when the
// page's own offsets still leave part of it outside. Answers the cap.
export const fitMembersMenu = async (options: {
  windowHeight: number;
  toggle: Box;
  menu: Box & { getAttribute: (name: string) => string | null };
  list: Box & { style: { maxHeight: string } };
  place: () => Promise<void> | void;
}) => {
  const { windowHeight, toggle, menu, list, place } = options;
  const setCap = (cap: number) => {
    if (list.style.maxHeight !== `${cap}px`) {
      list.style.maxHeight = `${cap}px`;
    }
  };
  let cap = membersListHeight({
    windowHeight,
    toggle: toggle.getBoundingClientRect(),
    menu: menu.getBoundingClientRect(),
    list: list.getBoundingClientRect(),
    above: `${menu.getAttribute("data-popper-placement") || ""}`.startsWith(
      "top"
    )
  });
  setCap(cap);
  await place();
  // Bootstrap's dropdown measures with its own offsets, not the page's, and
  // may move it to the other side once it is smaller: a few more passes
  for (let pass = 0; pass < 4 && cap > MEMBERS_LIST_MIN; pass++) {
    const over = outsideWindow(menu.getBoundingClientRect(), windowHeight);
    if (over <= 0) break;
    cap = Math.max(MEMBERS_LIST_MIN, cap - over);
    setCap(cap);
    await place();
  }
  return cap;
};
